import { useState, useEffect } from 'react'
import {
  Chip,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { makeStyles } from 'tss-react/mui'
import {
  CharacteristicConfig,
  ContextModel,
  EmpiricalConceptConfig,
} from '../../shared/model/models'
import { configurationAPI } from '../../shared/api/ConfigurationAPI'
import { contextModelAPI } from '../../shared/api/ContextModelAPI'

type FsDescriptiveCharacteristicsSelectProps = {
  onSuccess?: () => void
  onError?: () => void
  contextModel: ContextModel
}

const useStyles = makeStyles()(() => ({
  error: {
    color: '#f44336',
  },
  menu: {
    maxHeight: '60%',
  },
}))

const FsDescriptiveCharacteristicsSelect = (
  props: FsDescriptiveCharacteristicsSelectProps,
) => {
  const { classes } = useStyles()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onSuccess, onError, contextModel } = props
  const [empiricalConceptConfigs, setEmpiricalConceptConfigs] = useState<EmpiricalConceptConfig[]>(
    [],
  )
  const [empiricalConceptErrors, setEmpiricalConceptErrors] = useState<number[]>([])
  const [descriptiveCharacteristicConfigs, setDescriptiveCharacteristicConfigs] = useState<
    CharacteristicConfig[]
  >([])
  const [
    selectedDescriptiveCharacteristicIdsAsString,
    setSelectedDescriptiveCharacteristicIdsAsString,
  ] = useState<string[]>([])

  useEffect(() => {
    configurationAPI
      .empiricalConceptConfigForDescriptiveCharacteristics(contextModel.id)
      .then((res: EmpiricalConceptConfig[]) => {
        setEmpiricalConceptConfigs(res)
        let allDescriptiveCharacteristicConfigs: CharacteristicConfig[] = []
        res.forEach((it) => {
          if (Array.isArray(it.feasibleCharacteristics)) {
            allDescriptiveCharacteristicConfigs = allDescriptiveCharacteristicConfigs.concat(
              it.feasibleCharacteristics,
            )
          }
        })

        setDescriptiveCharacteristicConfigs(allDescriptiveCharacteristicConfigs)
        let autoSelected: string[] = []
        res.forEach((it) => {
          if (Array.isArray(it.feasibleCharacteristics)) {
            const aS = it.feasibleCharacteristics.filter((ittt) => ittt.autoSelection)
            if (aS.length > 0) {
              autoSelected = [...autoSelected, ...aS.map((itt) => `${itt.id}`)]
            } else {
              autoSelected.push(`${it.feasibleCharacteristics[0].id}`)
            }
            allDescriptiveCharacteristicConfigs = allDescriptiveCharacteristicConfigs.concat(
              it.feasibleCharacteristics,
            )
          }
        })

        setSelectedDescriptiveCharacteristicIdsAsString(autoSelected)
      }).catch(() => onError?.())
  }, [])

  const selectedDescriptiveCharacteristics = (value: string[]) =>
    descriptiveCharacteristicConfigs.filter((it) => value.includes(`${it.id}`))

  const empiricalConceptIdForCharacteristic = (characteristicId: number) =>
    empiricalConceptConfigs.filter((ecc) =>
      ecc.feasibleCharacteristics.map((ic) => ic.id).includes(characteristicId),
    )[0].empiricalConcept.id

  const validateRequiredSelection = (toValidate: string[]) => {
    const a = selectedDescriptiveCharacteristics(toValidate)
    const err: number[] = []
    empiricalConceptConfigs.forEach((ecc) => {
      if (ecc.mandatory) {
        const result = ecc.feasibleCharacteristics
          .map((c) => c.id)
          .filter((value) => a.map((sc) => sc.id).includes(value))
        if (result.length === 0) {
          err.push(ecc.empiricalConcept.id)
        }
      }
    })
    setEmpiricalConceptErrors(err)
    return err.length === 0
  }

  const validateMultiSelection = (actualSelected: string[], newSelected: string[]) => {
    if (newSelected.length < actualSelected.length) return newSelected
    const lastSelectedValue = Number(
      newSelected.filter((value) => !actualSelected.includes(value))[0],
    )
    const empiricalConceptId = empiricalConceptIdForCharacteristic(lastSelectedValue)
    const validatedSelection: string[] = newSelected
    empiricalConceptConfigs
      .filter((ecc) => ecc.empiricalConcept.id === empiricalConceptId)
      .forEach((config) => {
        const result = config.feasibleCharacteristics
          .filter((fic) => fic.operation !== 'SQUARED')
          .map((c) => c.id)
          .filter((value) => validatedSelection.includes(`${value}`))
        if (result.length > 1) {
          const idx = validatedSelection.indexOf(
            `${result.filter((x) => x !== lastSelectedValue)[0]}`,
          )
          validatedSelection.splice(idx, 1)
        }
      })
    return validatedSelection
  }

  const onDescriptiveCharacteristicsChange = (e: SelectChangeEvent<string[] | string>) => {
    const newSelection = typeof e.target.value === 'string' ? [e.target.value] : e.target.value
    validateRequiredSelection(newSelection)
    const validatedSelected = validateMultiSelection(
      selectedDescriptiveCharacteristicIdsAsString,
      newSelection,
    )
    setSelectedDescriptiveCharacteristicIdsAsString(validatedSelected)
  }

  const saveDescriptiveCharacteristics = () => {
    validateRequiredSelection(selectedDescriptiveCharacteristicIdsAsString)
    contextModelAPI
      .saveDescriptiveCharacteristicsForContextModel(
        contextModel,
        selectedDescriptiveCharacteristicIdsAsString.map((it) => ({
          characteristicId: Number(it),
        })),
      )
      .then(() => onSuccess?.())
      .catch(() => onError?.())
  }

  const renderSelectGroup = (ecc: EmpiricalConceptConfig) => {
    const items = ecc.feasibleCharacteristics.map((characteristic) => (
      <MenuItem
        key={characteristic.id}
        value={`${characteristic.id}`}
        disabled={characteristic.autoSelection}
      >
        {characteristic.name}
      </MenuItem>
    ))

    return [
      <ListSubheader sx={{}}>
        <Typography
          variant="h3"
          color="primary"
          className={`${
            empiricalConceptErrors.includes(ecc.empiricalConcept.id) ? classes.error : ''
          }`}
        >
          {ecc.empiricalConcept.name} {ecc.mandatory ? '*' : null}
        </Typography>
      </ListSubheader>,
      items,
    ]
  }

  return (
    <>
      <FormControl sx={{ m: 1, width: '60%' }} size="small">
        <InputLabel
          id="descriptive-characteristics-label"
          sx={{ backgroundColor: '#FAF9F6', paddingRight: 1 }}
        >
          Descriptive Characteristics
        </InputLabel>
        <Select
          MenuProps={{ className: classes.menu }}
          value={selectedDescriptiveCharacteristicIdsAsString}
          sx={{
            maxHeight: '200px',
          }}
          multiple
          input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedDescriptiveCharacteristics(selected).map((c) => (
                <Chip key={`${c.id}`} label={c.name} />
              ))}
            </Box>
          )}
          id="descriptive-characteristics-select"
          labelId="descriptive-characteristics-label"
          label="wtf"
          onChange={onDescriptiveCharacteristicsChange}
        >
          {empiricalConceptConfigs?.map((ecc) => renderSelectGroup(ecc))}
        </Select>
      </FormControl>
      <Button onClick={saveDescriptiveCharacteristics} variant="contained">
        Submit
      </Button>
    </>
  )
}

export default FsDescriptiveCharacteristicsSelect
